import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  Clipboard,
  ExternalLink,
  FileSignature,
  Gauge,
  Layers,
  ShieldAlert,
  Trash2,
  TrendingDown,
} from "lucide-react";
import { DashboardShell, LoadingPanel, PageHeading } from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import {
  assessGspPlusExposure,
  buildStatementOnOrigin,
  GRADUATION_NOTE,
  GSP_PLUS_CONVENTIONS,
  GSP_REGULATION,
  loadTariffStore,
  preferenceUtilisation,
  scanGspPlusPortfolio,
  UZBEKISTAN_GSP_PLUS,
  WITHDRAWAL_GROUNDS,
  type CumulationKind,
  type ExposureLine,
  type StatementLanguage,
  type TariffStore,
} from "@/lib/eu";

export const Route = createFileRoute("/gsp-plus")({
  head: () => ({
    meta: [
      { title: "GSP+ hub — Ready4Trade" },
      {
        name: "description",
        content:
          "What EU GSP+ is worth to an Uzbek exporter: preference margins across your product pipeline, withdrawal exposure, utilisation, REX registration and a statement-on-origin generator.",
      },
    ],
  }),
  component: GspPlusPage,
});

const eur = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);

function GspPlusPage() {
  const { leads, isLoading } = useTradeData();
  const [store, setStore] = useState<TariffStore>();

  useEffect(() => {
    loadTariffStore().then(setStore, () => setStore(undefined));
  }, []);

  const [consignmentValue, setConsignmentValue] = useState(25000);
  const scan = useMemo(
    () =>
      store && leads
        ? scanGspPlusPortfolio(
            leads.map((lead) => ({ h6: lead.h6, dc: lead.dc })),
            store,
            consignmentValue,
          )
        : undefined,
    [store, leads, consignmentValue],
  );

  if (isLoading || !store) {
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <PageHeading
        title="🇺🇿 GSP+ — what the preference is worth, and what it rests on"
        subtitle="Uzbekistan's GSP+ status removes duty on most EU tariff lines. It is also conditional, monitored and revocable. This page prices both sides of that."
        badge="Regulation (EU) No 978/2012"
      />

      <StatusPanel />

      {scan && (
        <PortfolioPanel scan={scan} value={consignmentValue} onValueChange={setConsignmentValue} />
      )}

      <ExposurePanel />

      <UtilisationPanel />

      <StatementPanel />

      <ConventionsPanel />
    </DashboardShell>
  );
}

function StatusPanel() {
  return (
    <section className="surface-panel p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <BadgeCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg">Beneficiary status</h2>
            <p className="text-xs text-muted-foreground">
              {UZBEKISTAN_GSP_PLUS.country} · GSP+ since {UZBEKISTAN_GSP_PLUS.since ?? "—"}
            </p>
          </div>
        </div>
        <a
          href={GSP_REGULATION.eurLexUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium"
        >
          <ExternalLink className="h-3.5 w-3.5" /> CELEX {GSP_REGULATION.celex}
        </a>
      </div>

      <ul className="mt-4 space-y-2 text-xs leading-relaxed text-muted-foreground">
        {UZBEKISTAN_GSP_PLUS.monitoringNotes.map((note) => (
          <li key={note}>• {note}</li>
        ))}
      </ul>

      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900">
        <div className="flex items-center gap-2 font-semibold">
          <AlertTriangle className="h-4 w-4" /> Status and scheme validity are unverified in this
          build
        </div>
        <p className="mt-1">
          Beneficiary status, the scheme's end date and the Annex VIII list are all held as data
          with a verification flag rather than asserted. Confirm each against EUR-Lex and the
          Commission's GSP pages before advising a client. {GRADUATION_NOTE.verifyPrompt}
        </p>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <ShieldAlert className="h-3.5 w-3.5" /> How GSP+ can be lost
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {WITHDRAWAL_GROUNDS.map((ground) => (
            <div key={ground.id} className="rounded-lg bg-secondary/60 p-3 text-xs">
              <div className="font-semibold text-foreground">{ground.label}</div>
              <p className="mt-1 leading-relaxed text-muted-foreground">{ground.effect}</p>
              <div className="mt-1 text-[10px] text-muted-foreground">{ground.legalBasis}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PortfolioPanel({
  scan,
  value,
  onValueChange,
}: {
  scan: ReturnType<typeof scanGspPlusPortfolio>;
  value: number;
  onValueChange: (value: number) => void;
}) {
  return (
    <section className="surface-panel p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg">GSP+ across your buyer-lead pipeline</h2>
            <p className="text-xs text-muted-foreground">
              Every EU-destined lead in the dataset, matched to a CN8 line and priced at the GSP+
              margin.
            </p>
          </div>
        </div>
        <label className="text-xs">
          <span className="mb-1 block font-semibold">Assumed consignment value (EUR)</span>
          <input
            type="number"
            min={0}
            step={1000}
            value={value}
            onChange={(event) => onValueChange(globalThis.Number(event.target.value) || 0)}
            className="h-10 w-44 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50"
          />
        </label>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="EU-destined leads"
          value={scan.euLeads.toLocaleString("en-US")}
          note={`of ${scan.totalLeads.toLocaleString("en-US")} total`}
        />
        <Metric
          label="Matched to a CN8 line"
          value={scan.matchedLeads.toLocaleString("en-US")}
          note={`${scan.unmatchedHs6.length} HS6 codes not yet in the tariff store`}
        />
        <Metric
          label="Distinct lines"
          value={String(scan.rows.length)}
          note="with a GSP+ margin computed"
        />
        <Metric
          label="Indicative GSP+ value"
          value={eur(scan.estimatedAnnualSavingEur)}
          note="if every matched lead converted"
          tone="good"
        />
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-secondary text-muted-foreground">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">CN8 / product</th>
              <th className="px-3 py-2 text-right font-semibold">Leads</th>
              <th className="px-3 py-2 text-right font-semibold">MFN</th>
              <th className="px-3 py-2 text-right font-semibold">GSP+</th>
              <th className="px-3 py-2 text-right font-semibold">Margin</th>
              <th className="px-3 py-2 text-right font-semibold">Indicative value</th>
            </tr>
          </thead>
          <tbody>
            {scan.rows.map((row) => (
              <tr key={row.cn8} className="border-t border-border">
                <td className="px-3 py-2">
                  <span className="code-chip text-primary">{row.cn8}</span>
                  <div className="mt-0.5 max-w-md truncate text-muted-foreground">
                    {row.description}
                  </div>
                  {row.specificDutyRetained && (
                    <div className="mt-0.5 text-[10px] text-amber-700">
                      Specific duty retained under Art. 12(2) — margin is the ad valorem part only
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{row.leads}</td>
                <td className="px-3 py-2 text-right">{row.mfnRatePercent}%</td>
                <td className="px-3 py-2 text-right">{row.gspPlusRatePercent}%</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                  {row.marginPoints > 0 ? `−${row.marginPoints} pp` : "—"}
                </td>
                <td className="px-3 py-2 text-right">{eur(row.estimatedSavingEur)}</td>
              </tr>
            ))}
            {!scan.rows.length && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  No EU-destined lead matched a line in the tariff store yet. The seed store covers
                  Uzbekistan's principal export lines only.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ul className="mt-3 space-y-1 text-[11px] leading-relaxed text-muted-foreground">
        {scan.caveats.map((caveat) => (
          <li key={caveat}>• {caveat}</li>
        ))}
      </ul>

      <p className="mt-3 text-xs text-muted-foreground">
        Price a specific consignment, with VAT, entry price and clearance requirements, in the{" "}
        <Link to="/eu-tariff" className="font-semibold text-primary">
          EU duty &amp; GSP+ calculator
        </Link>
        .
      </p>
    </section>
  );
}

const DEFAULT_EXPOSURE: ExposureLine[] = [
  { label: "Dried apricots", annualValueEur: 1200000, mfnRatePercent: 5.6, gspPlusRatePercent: 0 },
  { label: "Cotton t-shirts", annualValueEur: 800000, mfnRatePercent: 12, gspPlusRatePercent: 0 },
  { label: "Urea", annualValueEur: 3000000, mfnRatePercent: 6.5, gspPlusRatePercent: 0 },
];

function ExposurePanel() {
  const [lines, setLines] = useState<ExposureLine[]>(DEFAULT_EXPOSURE);
  const result = useMemo(() => assessGspPlusExposure(lines), [lines]);

  const update = (index: number, patch: Partial<ExposureLine>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  return (
    <section className="surface-panel p-5 md:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700">
          <TrendingDown className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-lg">Withdrawal exposure</h2>
          <p className="text-xs text-muted-foreground">
            What your own book would cost if GSP+ ended. Enter annual values by product line.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {lines.map((line, index) => (
          <div key={index} className="grid grid-cols-12 items-end gap-2">
            <label className="col-span-12 sm:col-span-4">
              <span className="mb-1 block text-[11px] font-semibold">Product line</span>
              <input
                value={line.label}
                onChange={(event) => update(index, { label: event.target.value })}
                className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs"
              />
            </label>
            <label className="col-span-5 sm:col-span-3">
              <span className="mb-1 block text-[11px] font-semibold">Annual EU value (EUR)</span>
              <input
                type="number"
                min={0}
                value={line.annualValueEur}
                onChange={(event) =>
                  update(index, { annualValueEur: globalThis.Number(event.target.value) || 0 })
                }
                className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs"
              />
            </label>
            <label className="col-span-3 sm:col-span-2">
              <span className="mb-1 block text-[11px] font-semibold">MFN %</span>
              <input
                type="number"
                min={0}
                step="0.1"
                value={line.mfnRatePercent}
                onChange={(event) =>
                  update(index, { mfnRatePercent: globalThis.Number(event.target.value) || 0 })
                }
                className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs"
              />
            </label>
            <label className="col-span-3 sm:col-span-2">
              <span className="mb-1 block text-[11px] font-semibold">GSP+ %</span>
              <input
                type="number"
                min={0}
                step="0.1"
                value={line.gspPlusRatePercent}
                onChange={(event) =>
                  update(index, { gspPlusRatePercent: globalThis.Number(event.target.value) || 0 })
                }
                className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs"
              />
            </label>
            <button
              onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
              aria-label="Remove line"
              className="col-span-1 flex h-9 items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={() =>
          setLines((current) => [
            ...current,
            { label: "New line", annualValueEur: 0, mfnRatePercent: 0, gspPlusRatePercent: 0 },
          ])
        }
        className="mt-3 rounded-lg bg-secondary px-3 py-2 text-xs font-medium"
      >
        Add product line
      </button>

      <div className="mt-5 rounded-xl bg-slate-950 p-5 text-white">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/55">
              Annual duty GSP+ removes today
            </div>
            <div className="mt-1 text-3xl font-bold">{eur(result.annualSavingEur)}</div>
            <div className="mt-1 text-[11px] text-white/60">
              {result.savingRatePercent}% of {eur(result.annualValueEur)} shipped — the margin you
              would have to find elsewhere if the preference ended
            </div>
          </div>
        </div>
      </div>

      {result.lines.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {result.lines.map((line) => (
            <div key={line.label} className="flex items-center gap-3 text-xs">
              <div className="w-40 shrink-0 truncate">{line.label}</div>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${line.shareOfSaving}%` }}
                />
              </div>
              <div className="w-24 shrink-0 text-right font-medium">{eur(line.savingEur)}</div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-blue-900">
        {result.note}
      </p>
    </section>
  );
}

function UtilisationPanel() {
  const [eligible, setEligible] = useState(1000000);
  const [claimed, setClaimed] = useState(620000);
  const result = useMemo(
    () => preferenceUtilisation({ eligibleValueEur: eligible, claimedValueEur: claimed }),
    [eligible, claimed],
  );

  const tone =
    result.verdict === "strong"
      ? "text-emerald-700"
      : result.verdict === "adequate"
        ? "text-amber-700"
        : "text-red-700";

  return (
    <section className="surface-panel p-5 md:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
          <Gauge className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-lg">Preference utilisation</h2>
          <p className="text-xs text-muted-foreground">
            Of the value that could have claimed GSP+, how much actually did.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label>
          <span className="mb-1 block text-[11px] font-semibold">
            Eligible EU-bound value (EUR)
          </span>
          <input
            type="number"
            min={0}
            value={eligible}
            onChange={(event) => setEligible(globalThis.Number(event.target.value) || 0)}
            className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
          />
        </label>
        <label>
          <span className="mb-1 block text-[11px] font-semibold">
            Value on which preference was claimed (EUR)
          </span>
          <input
            type="number"
            min={0}
            value={claimed}
            onChange={(event) => setClaimed(globalThis.Number(event.target.value) || 0)}
            className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Utilisation rate
          </div>
          <div className={`text-3xl font-bold ${tone}`}>{result.rate}%</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Value shipped without preference
          </div>
          <div className="text-xl font-semibold">{eur(result.forgoneValueEur)}</div>
        </div>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{result.message}</p>
    </section>
  );
}

function StatementPanel() {
  const [form, setForm] = useState({
    exporterName: "",
    exporterAddress: "",
    rexNumber: "",
    originCountry: "Uzbekistan",
    whollyObtained: true,
    hsHeading: "0813",
    cumulation: "none" as CumulationKind,
    extendedCumulationCountry: "",
    consignmentValueEur: 40000,
    place: "",
    date: new Date().toISOString().slice(0, 10),
    language: "en" as StatementLanguage,
  });
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => buildStatementOnOrigin(form), [form]);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  async function copy() {
    if (!result.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <section className="surface-panel p-5 md:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <FileSignature className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-lg">Statement on origin generator</h2>
          <p className="text-xs text-muted-foreground">
            Annex 22-07 wording. Since the EU withdrew Form A, this paragraph on your invoice is the
            preference claim — there is no certificate behind it.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Text
            label="Exporter name"
            value={form.exporterName}
            onChange={(v) => set("exporterName", v)}
          />
          <Text
            label="REX number"
            value={form.rexNumber}
            onChange={(v) => set("rexNumber", v)}
            placeholder="UZREX0001234"
          />
          <div className="sm:col-span-2">
            <Text
              label="Registered address"
              value={form.exporterAddress}
              onChange={(v) => set("exporterAddress", v)}
            />
          </div>
          <Text
            label="Country of origin"
            value={form.originCountry}
            onChange={(v) => set("originCountry", v)}
          />
          <label>
            <span className="mb-1 block text-[11px] font-semibold">Consignment value (EUR)</span>
            <input
              type="number"
              min={0}
              value={form.consignmentValueEur}
              onChange={(event) =>
                set("consignmentValueEur", globalThis.Number(event.target.value) || 0)
              }
              className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
            />
          </label>
          <Text label="Place" value={form.place} onChange={(v) => set("place", v)} />
          <label>
            <span className="mb-1 block text-[11px] font-semibold">Date</span>
            <input
              type="date"
              value={form.date}
              onChange={(event) => set("date", event.target.value)}
              className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
            />
          </label>
          <label>
            <span className="mb-1 block text-[11px] font-semibold">Language</span>
            <select
              value={form.language}
              onChange={(event) => set("language", event.target.value as StatementLanguage)}
              className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
            </select>
          </label>
          <label>
            <span className="mb-1 block text-[11px] font-semibold">Cumulation</span>
            <select
              value={form.cumulation}
              onChange={(event) => set("cumulation", event.target.value as CumulationKind)}
              className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
            >
              <option value="none">None</option>
              <option value="eu_cumulation">EU cumulation</option>
              <option value="regional_cumulation">Regional cumulation</option>
              <option value="extended_cumulation">Extended cumulation</option>
              <option value="norway_cumulation">Norway cumulation</option>
              <option value="switzerland_cumulation">Switzerland cumulation</option>
              <option value="turkiye_cumulation">Türkiye cumulation</option>
            </select>
          </label>
          {form.cumulation === "extended_cumulation" && (
            <Text
              label="Extended cumulation country"
              value={form.extendedCumulationCountry}
              onChange={(v) => set("extendedCumulationCountry", v)}
            />
          )}
          <div className="sm:col-span-2 space-y-2 border-t border-border pt-3">
            <label className="flex cursor-pointer items-center gap-2.5 text-xs">
              <input
                type="checkbox"
                checked={form.whollyObtained}
                onChange={(event) => set("whollyObtained", event.target.checked)}
                className="h-4 w-4 accent-[color:var(--color-primary)]"
              />
              <span>Wholly obtained in the country of origin (criterion P)</span>
            </label>
            {!form.whollyObtained && (
              <Text
                label="HS heading of the finished product (criterion W)"
                value={form.hsHeading}
                onChange={(v) => set("hsHeading", v)}
                placeholder="2007"
              />
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-secondary/40 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Statement · criterion {result.originCriterion}
              </span>
              <button
                onClick={() => void copy()}
                disabled={!result.text}
                className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium disabled:opacity-40"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <Clipboard className="h-3.5 w-3.5" />
                )}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {result.text ? (
              <p className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-foreground">
                {result.text}
              </p>
            ) : (
              <p className="mt-3 text-xs italic leading-relaxed text-muted-foreground">
                Resolve the blocking issues below before the statement can be made out. A defective
                statement is worse than none: it invites a refusal after the goods have shipped.
              </p>
            )}
            {result.validUntil && result.text && (
              <p className="mt-3 border-t border-border pt-2 text-[10px] text-muted-foreground">
                Valid for 12 months — until {result.validUntil}.
              </p>
            )}
          </div>

          {result.issues.length > 0 && (
            <ul className="space-y-2">
              {result.issues.map((issue, index) => (
                <li
                  key={`${issue.field}-${index}`}
                  className={`rounded-lg border p-3 text-xs leading-relaxed ${
                    issue.severity === "blocking"
                      ? "border-red-200 bg-red-50 text-red-900"
                      : "border-amber-200 bg-amber-50 text-amber-900"
                  }`}
                >
                  <strong className="capitalize">{issue.severity}</strong> · {issue.message}
                </li>
              ))}
            </ul>
          )}

          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Wording is reproduced from {result.provenance.source} and is marked{" "}
            {result.provenance.status.replace(/_/g, " ")}. Confirm the current Annex text before
            putting this on a commercial invoice.
          </p>
        </div>
      </div>
    </section>
  );
}

function ConventionsPanel() {
  const grouped = useMemo(() => {
    const map = new Map<string, typeof GSP_PLUS_CONVENTIONS>();
    for (const convention of GSP_PLUS_CONVENTIONS) {
      map.set(convention.cluster, [...(map.get(convention.cluster) ?? []), convention]);
    }
    return [...map];
  }, []);

  const labels: Record<string, string> = {
    human_rights: "Human rights",
    labour: "Labour rights",
    environment: "Environment and climate",
    governance: "Narcotics control and governance",
  };

  return (
    <section className="surface-panel p-5 md:p-6">
      <h2 className="font-display text-lg">The 27 conventions GSP+ rests on</h2>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Annex VIII to the Regulation. Uzbekistan's preference is conditional on ratifying and
        effectively implementing all of them, and on accepting monitoring. This is why the status is
        reviewable, and why an exporter's duty saving is ultimately a policy variable.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {grouped.map(([cluster, conventions]) => (
          <div key={cluster}>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {labels[cluster]} · {conventions.length}
            </div>
            <ul className="space-y-1.5">
              {conventions.map((convention) => (
                <li key={convention.id} className="rounded-lg bg-secondary/50 px-3 py-2 text-xs">
                  <span className="text-foreground">{convention.name}</span>
                  <span className="ml-1.5 text-[10px] text-muted-foreground">
                    ({convention.year})
                  </span>
                  <span className="ml-1.5 rounded bg-secondary px-1 text-[9px] font-semibold text-muted-foreground">
                    Part {convention.annexPart}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  note,
  tone = "neutral",
}: {
  label: string;
  value: string;
  note: string;
  tone?: "neutral" | "good";
}) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-bold ${tone === "good" ? "text-emerald-700" : ""}`}>
        {value}
      </div>
      <div className="mt-1 text-[10px] leading-snug text-muted-foreground">{note}</div>
    </div>
  );
}

function Text({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold">{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50"
      />
    </label>
  );
}
